import maplibregl from 'maplibre-gl';
import BaseProduct from './BaseProduct';
import LayerManager from './LayerManager';
import { AppState } from '../state';
import store from '../state';
import { ProductId, WxStationFeatureProps, WxStationMetric } from '../types';
import { appendAuthToken, cleanUrl, getApiServer } from '../config';
import { wxStationModal } from '../ui/panels/WxStationModal';

const SOURCE_ID = 'wx-stations-source';
const DOTS_LAYER_ID = 'wx-stations-dots';
const LABELS_LAYER_ID = 'wx-stations-labels';

export class WxStationsLayer extends BaseProduct {
  public readonly id: ProductId = 'wx_stations';
  private hoverPopup: maplibregl.Popup | null = null;
  private cachedGeoJson: any | null = null;
  private lastFetchedMtime: number | null = null;
  private fetchInFlight: Promise<void> | null = null;
  private interactionsBound: boolean = false;
  private activeMetric: WxStationMetric | null = null;
  private lastUnits: 'imperial' | 'metric' = 'imperial';
  private lastOpacity: number = 1.0;

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);
    WxStationsLayer.injectBrandedCheckboxStyles();

    this.map.on('style.load', () => {
      if (this.activeMetric && this.cachedGeoJson) {
        this.ensureLayersAndApplyStyle();
      }
    });

    store.subscribe((state) => {
      this.syncWithState(state);
    });
  }

  private static injectBrandedCheckboxStyles(): void {
    if (typeof document === 'undefined' || document.getElementById('gw-branded-checkbox-styles')) return;
    const style = document.createElement('style');
    style.id = 'gw-branded-checkbox-styles';
    style.textContent = `
      .gw-wx-overlay-label input[type="checkbox"],
      .gw-branded-checkbox-label input[type="checkbox"] {
        accent-color: #38bdf8;
        color-scheme: dark;
      }
      @supports not (accent-color: #38bdf8) {
        .gw-wx-overlay-label,
        .gw-branded-checkbox-label {
          position: relative;
          padding-left: 1.45rem;
        }
        .gw-wx-overlay-label input[type="checkbox"],
        .gw-branded-checkbox-label input[type="checkbox"] {
          position: absolute !important;
          clip-path: inset(50%) !important;
          overflow: hidden !important;
          width: 1px !important;
          height: 1px !important;
          margin: -1px !important;
          padding: 0 !important;
          border: 0 !important;
          white-space: nowrap !important;
        }
        .gw-wx-overlay-label input[type="checkbox"] + span::before,
        .gw-branded-checkbox-label input[type="checkbox"] + span::before {
          content: "";
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 0.95rem;
          height: 0.95rem;
          border: 1.5px solid #94a3b8;
          background: rgba(15, 23, 42, 0.9);
          border-radius: 3px;
          box-sizing: border-box;
        }
        .gw-wx-overlay-label input[type="checkbox"]:focus-visible + span::before,
        .gw-branded-checkbox-label input[type="checkbox"]:focus-visible + span::before {
          outline: 2px solid #38bdf8;
          outline-offset: 2px;
        }
        .gw-wx-overlay-label input[type="checkbox"]:checked + span::before,
        .gw-branded-checkbox-label input[type="checkbox"]:checked + span::before {
          background-color: #38bdf8;
          border-color: #38bdf8;
        }
        .gw-wx-overlay-label input[type="checkbox"]:checked + span::after,
        .gw-branded-checkbox-label input[type="checkbox"]:checked + span::after {
          content: "✓";
          position: absolute;
          left: 0.16rem;
          top: 50%;
          transform: translateY(-52%);
          color: #090d16;
          font-size: 0.72rem;
          font-weight: 900;
          line-height: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }

  public static resolveEffectiveMetric(state: AppState): WxStationMetric | null {
    const isStandalone = Boolean(
      state.wxStationsEnabled ||
      state.primaryProduct === 'wx_stations' ||
      (state.activeLayerProducts && state.activeLayerProducts['wx_stations'] === true)
    );
    if (isStandalone) {
      return state.wxStationsMetric || 'temp';
    }

    if (state.wxStationsOverlayEnabled === false) {
      return null;
    }

    const isCurrentTempActive = Boolean(
      state.primaryProduct === 'hrtemp_current' ||
      state.liveWeatherTempGradientEnabled ||
      (state.activeLayerProducts && state.activeLayerProducts['hrtemp_current'] === true)
    );
    const isCurrentWindActive = Boolean(
      state.liveWeatherRtmaWindsEnabled ||
      (state.activeLayerProducts && (
        state.activeLayerProducts['winds_surface_current'] === true ||
        state.activeLayerProducts['rtma_winds'] === true
      ))
    );
    const isRhActive = Boolean(
      state.primaryProduct === 'relative_humidity' ||
      (state.activeLayerProducts && state.activeLayerProducts['relative_humidity'] === true)
    );
    const isDewpointActive = Boolean(
      state.primaryProduct === 'dewpoint' ||
      (state.activeLayerProducts && state.activeLayerProducts['dewpoint'] === true)
    );
    const isWindchillActive = Boolean(
      state.primaryProduct === 'windchill' ||
      (state.activeLayerProducts && state.activeLayerProducts['windchill'] === true)
    );

    const last = state.lastActivatedProduct;
    if (last === 'winds_surface_current' || last === 'rtma_winds' || (last === 'winds' && isCurrentWindActive)) {
      if (isCurrentWindActive) return 'wind';
    }
    if (last === 'hrtemp_current' && isCurrentTempActive) return 'temp';
    if (last === 'relative_humidity' && isRhActive) return 'rh';
    if (last === 'dewpoint' && isDewpointActive) return 'dewpoint';
    if (last === 'windchill' && isWindchillActive) return 'feels_like';

    if (isCurrentTempActive) return 'temp';
    if (isCurrentWindActive) return 'wind';
    if (isRhActive) return 'rh';
    if (isDewpointActive) return 'dewpoint';
    if (isWindchillActive) return 'feels_like';

    return null;
  }

  public syncWithState(state: AppState): void {
    const nextMetric = WxStationsLayer.resolveEffectiveMetric(state);
    const nextUnits = state.units || 'imperial';
    const nextOpacity = state.layerOpacities?.['wx_stations'] ?? 1.0;
    const manifestMtime = state.manifest?.wx_stations?.mtime_epoch ?? null;

    if (nextMetric === null) {
      if (this.activeMetric !== null || this.active) {
        this.activeMetric = null;
        this.active = false;
        this.setVisibility(false);
        this.clearPopup();
      }
      return;
    }

    const metricOrUnitsChanged = nextMetric !== this.activeMetric || nextUnits !== this.lastUnits;
    const opacityChanged = Math.abs(nextOpacity - this.lastOpacity) > 0.01;
    const mtimeChanged = manifestMtime !== null && manifestMtime !== this.lastFetchedMtime;

    this.activeMetric = nextMetric;
    this.lastUnits = nextUnits;
    this.lastOpacity = nextOpacity;
    this.active = true;

    if (!this.cachedGeoJson || mtimeChanged) {
      void this.fetchAndUpdateGeoJson(state, manifestMtime);
    }

    if (this.cachedGeoJson) {
      if (!this.map.getSource(SOURCE_ID) || !this.map.getLayer(DOTS_LAYER_ID) || !this.map.getLayer(LABELS_LAYER_ID)) {
        this.ensureLayersAndApplyStyle();
      } else {
        if (metricOrUnitsChanged) {
          this.applyMetricStyle(nextMetric, nextUnits);
        }
        if (opacityChanged) {
          this.applyOpacity(nextOpacity);
        }
        this.setVisibility(true);
      }
    }
  }

  protected onActivate(): void {
    this.syncWithState(store.getState());
  }

  protected onDeactivate(): void {
    this.syncWithState(store.getState());
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    // Real-time METAR surface station observations
  }

  private async fetchAndUpdateGeoJson(state: AppState, targetMtime: number | null): Promise<void> {
    if (this.fetchInFlight) return;

    const manifest = state.manifest;
    const apiServer = getApiServer(manifest);
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const cacheBust = targetMtime || Math.floor(Date.now() / 300000);
    const rawUrl = cleanUrl(`${apiServer}/${tileRoot}/observed/wx_stations/wx_stations.json?_t=${cacheBust}`);
    const url = appendAuthToken(rawUrl);

    this.fetchInFlight = (async () => {
      try {
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!data || data.type !== 'FeatureCollection' || !Array.isArray(data.features)) return;

        this.cachedGeoJson = data;
        this.lastFetchedMtime = targetMtime ?? cacheBust;

        const existingSrc = this.map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        if (existingSrc && typeof existingSrc.setData === 'function') {
          existingSrc.setData(data);
        }

        if (this.activeMetric) {
          this.ensureLayersAndApplyStyle();
        }
      } catch (err) {
        console.warn('[WxStationsLayer] Failed to fetch wx_stations.json:', err);
      } finally {
        this.fetchInFlight = null;
      }
    })();

    await this.fetchInFlight;
  }

  private ensureLayersAndApplyStyle(): void {
    if (!this.cachedGeoJson || !this.activeMetric) return;
    if (!this.map.getStyle()) return;

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: this.cachedGeoJson,
        cluster: false,
        maxzoom: 12,
        buffer: 128,
        tolerance: 0
      });
    }

    const metric = this.activeMetric;
    const units = this.lastUnits;
    const filterExpr = this.buildFilterExpression(metric, units);
    const colorExpr = this.buildColorExpression(metric);
    const textExpr = this.buildTextExpression(metric, units);

    if (!this.map.getLayer(DOTS_LAYER_ID)) {
      this.map.addLayer({
        id: DOTS_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: filterExpr,
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            3, 3.0,
            6, 4.2,
            10, 5.6
          ] as any,
          'circle-color': colorExpr as any,
          'circle-stroke-color': '#020617',
          'circle-stroke-width': 1.3,
          'circle-opacity': this.lastOpacity * 0.95,
          'circle-stroke-opacity': this.lastOpacity * 0.9
        }
      });
    }

    if (!this.map.getLayer(LABELS_LAYER_ID)) {
      this.map.addLayer({
        id: LABELS_LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        filter: filterExpr,
        layout: {
          'text-field': textExpr as any,
          'text-font': ['Noto Sans Regular'],
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            3, 10.5,
            6, 11.5,
            10, 12.5
          ] as any,
          'symbol-sort-key': ['coalesce', ['get', 'rank'], 4] as any,
          'text-variable-anchor': ['bottom', 'top', 'right', 'left'],
          'text-radial-offset': 0.52,
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'text-padding': 4
        },
        paint: {
          'text-color': metric === 'flight_cat' ? (colorExpr as any) : '#f8fafc',
          'text-halo-color': 'rgba(2, 6, 23, 0.95)',
          'text-halo-width': 1.85,
          'text-halo-blur': 0.25,
          'text-opacity': this.lastOpacity
        }
      });
    }

    this.applyMetricStyle(metric, units);
    this.applyOpacity(this.lastOpacity);
    this.setVisibility(true);

    if (!this.interactionsBound) {
      this.bindInteractions();
      this.interactionsBound = true;
    }
  }

  private buildFilterExpression(metric: WxStationMetric, units: 'imperial' | 'metric'): any {
    const tiers = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.wx_stations?.zoom_tiers) || {};
    const r2Zoom = Number(tiers.rank2_zoom) || 5.0;
    const r3Zoom = Number(tiers.rank3_zoom) || 6.5;
    const r4Zoom = Number(tiers.rank4_zoom) || 8.0;

    const rankFilter: any = [
      '<=',
      ['coalesce', ['get', 'rank'], 4],
      ['step', ['zoom'], 1, r2Zoom, 2, r3Zoom, 3, r4Zoom, 4]
    ];

    const isMetric = units === 'metric';
    let hasFieldExpr: any = ['has', isMetric ? 'temp_c' : 'temp_f'];
    if (metric === 'wind') {
      hasFieldExpr = ['has', 'wspd_mph'];
    } else if (metric === 'precip') {
      hasFieldExpr = ['any', ['has', 'pcp_24h_in'], ['has', 'pcp_in']];
    } else if (metric === 'rh') {
      hasFieldExpr = ['has', 'rh'];
    } else if (metric === 'dewpoint') {
      hasFieldExpr = ['has', isMetric ? 'dew_c' : 'dew_f'];
    } else if (metric === 'feels_like') {
      hasFieldExpr = ['has', isMetric ? 'feels_c' : 'feels_f'];
    } else if (metric === 'pressure') {
      hasFieldExpr = isMetric ? ['has', 'slp_mb'] : ['has', 'altim_inhg'];
    } else if (metric === 'flight_cat') {
      hasFieldExpr = ['has', 'flt_cat'];
    }

    return ['all', rankFilter, hasFieldExpr];
  }

  private buildColorExpression(metric: WxStationMetric): any {
    if (metric === 'temp' || metric === 'feels_like') {
      const prop = metric === 'feels_like' ? 'feels_f' : 'temp_f';
      return [
        'interpolate', ['linear'],
        ['coalesce', ['get', prop], 50],
        -4, '#9333ea',
        14, '#3b82f6',
        32, '#06b6d4',
        50, '#22c55e',
        68, '#eab308',
        86, '#f97316',
        104, '#ef4444'
      ];
    }
    if (metric === 'wind') {
      return [
        'interpolate', ['linear'],
        ['coalesce', ['get', 'wspd_mph'], 0],
        0, '#94a3b8',
        8, '#38bdf8',
        18, '#22c55e',
        28, '#eab308',
        40, '#f97316',
        55, '#ef4444'
      ];
    }
    if (metric === 'precip') {
      return [
        'interpolate', ['linear'],
        ['coalesce', ['get', 'pcp_24h_in'], ['get', 'pcp_in'], 0],
        0, '#64748b',
        0.02, '#38bdf8',
        0.15, '#06b6d4',
        0.5, '#22c55e',
        1.25, '#eab308',
        2.5, '#f97316',
        4.0, '#ec4899'
      ];
    }
    if (metric === 'rh') {
      return [
        'interpolate', ['linear'],
        ['coalesce', ['get', 'rh'], 50],
        10, '#f59e0b',
        35, '#eab308',
        60, '#38bdf8',
        85, '#2563eb',
        100, '#1d4ed8'
      ];
    }
    if (metric === 'dewpoint') {
      return [
        'interpolate', ['linear'],
        ['coalesce', ['get', 'dew_f'], 45],
        20, '#93c5fd',
        40, '#38bdf8',
        55, '#22c55e',
        65, '#eab308',
        72, '#f97316',
        78, '#ef4444'
      ];
    }
    if (metric === 'pressure') {
      return [
        'interpolate', ['linear'],
        ['coalesce', ['get', 'slp_mb'], 1013.2],
        990, '#f43f5e',
        1004, '#f59e0b',
        1013, '#38bdf8',
        1024, '#818cf8',
        1036, '#c084fc'
      ];
    }
    // flight_cat
    return [
      'match',
      ['coalesce', ['get', 'flt_cat'], ''],
      'VFR', '#4ade80',
      'MVFR', '#60a5fa',
      'IFR', '#f87171',
      'LIFR', '#e879f9',
      '#94a3b8'
    ];
  }

  private buildTextExpression(metric: WxStationMetric, units: 'imperial' | 'metric'): any {
    const isMetric = units === 'metric';

    if (metric === 'temp') {
      const f = isMetric ? 'temp_c' : 'temp_f';
      return ['concat', ['to-string', ['round', ['get', f]]], '°'];
    }
    if (metric === 'feels_like') {
      const f = isMetric ? 'feels_c' : 'feels_f';
      return ['concat', ['to-string', ['round', ['get', f]]], '°'];
    }
    if (metric === 'dewpoint') {
      const f = isMetric ? 'dew_c' : 'dew_f';
      return ['concat', ['to-string', ['round', ['get', f]]], '°'];
    }
    if (metric === 'rh') {
      return ['concat', ['to-string', ['round', ['get', 'rh']]], '%'];
    }
    if (metric === 'wind') {
      const spdExpr: any = isMetric
        ? ['round', ['*', ['get', 'wspd_mph'], 1.60934]]
        : ['round', ['get', 'wspd_mph']];
      const gstExpr: any = isMetric
        ? ['round', ['*', ['coalesce', ['get', 'wgst_mph'], 0], 1.60934]]
        : ['round', ['coalesce', ['get', 'wgst_mph'], 0]];
      const unitStr = isMetric ? ' km/h' : ' mph';

      return [
        'case',
        ['<=', ['get', 'wspd_mph'], 0],
        'Calm',
        ['all', ['has', 'wgst_mph'], ['>', ['get', 'wgst_mph'], ['+', ['get', 'wspd_mph'], 4]]],
        ['concat', ['to-string', spdExpr], 'g', ['to-string', gstExpr], unitStr],
        ['concat', ['to-string', spdExpr], unitStr]
      ];
    }
    if (metric === 'precip') {
      if (isMetric) {
        const mmVal: any = [
          '/',
          ['round', ['*', ['coalesce', ['get', 'pcp_24h_mm'], ['get', 'pcp_mm'], 0], 10]],
          10
        ];
        return ['concat', ['to-string', mmVal], ' mm'];
      }
      const inVal: any = [
        '/',
        ['round', ['*', ['coalesce', ['get', 'pcp_24h_in'], ['get', 'pcp_in'], 0], 100]],
        100
      ];
      return ['concat', ['to-string', inVal], '"'];
    }
    if (metric === 'pressure') {
      if (isMetric) {
        return ['concat', ['to-string', ['round', ['get', 'slp_mb']]], ' mb'];
      }
      return ['concat', ['to-string', ['get', 'altim_inhg']], '"'];
    }
    return ['coalesce', ['get', 'flt_cat'], ''];
  }

  private applyMetricStyle(metric: WxStationMetric, units: 'imperial' | 'metric'): void {
    const filterExpr = this.buildFilterExpression(metric, units);
    const colorExpr = this.buildColorExpression(metric);
    const textExpr = this.buildTextExpression(metric, units);

    if (this.map.getLayer(DOTS_LAYER_ID)) {
      this.map.setFilter(DOTS_LAYER_ID, filterExpr);
      this.map.setPaintProperty(DOTS_LAYER_ID, 'circle-color', colorExpr);
    }
    if (this.map.getLayer(LABELS_LAYER_ID)) {
      this.map.setFilter(LABELS_LAYER_ID, filterExpr);
      this.map.setLayoutProperty(LABELS_LAYER_ID, 'text-field', textExpr);
      this.map.setPaintProperty(
        LABELS_LAYER_ID,
        'text-color',
        metric === 'flight_cat' ? colorExpr : '#f8fafc'
      );
    }
  }

  private applyOpacity(opacity: number): void {
    if (this.map.getLayer(DOTS_LAYER_ID)) {
      this.map.setPaintProperty(DOTS_LAYER_ID, 'circle-opacity', opacity * 0.95);
      this.map.setPaintProperty(DOTS_LAYER_ID, 'circle-stroke-opacity', opacity * 0.9);
    }
    if (this.map.getLayer(LABELS_LAYER_ID)) {
      this.map.setPaintProperty(LABELS_LAYER_ID, 'text-opacity', opacity);
    }
  }

  private setVisibility(visible: boolean): void {
    const v = visible ? 'visible' : 'none';
    if (this.map.getLayer(DOTS_LAYER_ID)) {
      this.map.setLayoutProperty(DOTS_LAYER_ID, 'visibility', v);
    }
    if (this.map.getLayer(LABELS_LAYER_ID)) {
      this.map.setLayoutProperty(LABELS_LAYER_ID, 'visibility', v);
    }
  }

  private clearPopup(): void {
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
  }

  private bindInteractions(): void {
    const targetLayers = [DOTS_LAYER_ID, LABELS_LAYER_ID];

    const onEnter = (e: maplibregl.MapLayerMouseEvent) => {
      this.map.getCanvas().style.cursor = 'pointer';
      const feature = e.features && e.features[0];
      if (!feature) return;
      const props = (feature.properties || {}) as WxStationFeatureProps;
      const coords = (feature.geometry as any).coordinates.slice();
      const isMetric = store.getState().units === 'metric';

      const tempStr = isMetric
        ? (props.temp_c != null ? `${Number(props.temp_c).toFixed(1)}°C` : null)
        : (props.temp_f != null ? `${Number(props.temp_f).toFixed(1)}°F` : null);
      const dewStr = isMetric
        ? (props.dew_c != null ? `${Number(props.dew_c).toFixed(1)}°C` : null)
        : (props.dew_f != null ? `${Number(props.dew_f).toFixed(1)}°F` : null);
      const rhStr = props.rh != null ? `${Math.round(Number(props.rh))}%` : null;

      let windStr: string | null = null;
      if (props.wspd_mph != null) {
        if (Number(props.wspd_mph) <= 0) {
          windStr = 'Calm';
        } else {
          const spd = isMetric ? `${Math.round(Number(props.wspd_mph) * 1.60934)} km/h` : `${Math.round(Number(props.wspd_mph))} mph`;
          const gst = (props.wgst_mph != null && Number(props.wgst_mph) > Number(props.wspd_mph))
            ? (isMetric ? ` (G ${Math.round(Number(props.wgst_mph) * 1.60934)})` : ` (G ${Math.round(Number(props.wgst_mph))})`)
            : '';
          windStr = `${spd}${gst}`;
        }
      }

      const pressStr = isMetric
        ? (props.slp_mb != null ? `${Number(props.slp_mb).toFixed(1)} mb` : null)
        : (props.altim_inhg != null ? `${Number(props.altim_inhg).toFixed(2)}"` : null);

      const pcp24In = props.pcp_24h_in != null ? Number(props.pcp_24h_in) : null;
      const pcp1In = props.pcp_in != null ? Number(props.pcp_in) : null;
      let precipStr: string | null = null;
      if (pcp24In !== null && Number.isFinite(pcp24In)) {
        precipStr = isMetric
          ? `${(pcp24In * 25.4).toFixed(1)} mm (24h)`
          : `${pcp24In.toFixed(2)}" (24h)`;
      } else if (pcp1In !== null && Number.isFinite(pcp1In)) {
        precipStr = isMetric
          ? `${(pcp1In * 25.4).toFixed(1)} mm (1h)`
          : `${pcp1In.toFixed(2)}" (1h)`;
      }

      const html = `
        <div style="font-family: system-ui, sans-serif; padding: 6px 9px; color: #f8fafc; background: rgba(15, 23, 42, 0.96); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 7px; box-shadow: 0 6px 18px rgba(0,0,0,0.55); min-width: 195px;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 3px;">
            <span style="font-weight: 800; font-size: 12.5px; color: #38bdf8;">📡 ${props.name || props.id}</span>
            <span style="font-size: 10.5px; font-weight: 800; color: #94a3b8;">${props.id}</span>
          </div>
          <div style="display: flex; flex-wrap: wrap; gap: 8px 12px; font-size: 11.5px; margin-top: 4px;">
            ${tempStr ? `<div><strong>Temp:</strong> <span style="color: #fb923c;">${tempStr}</span></div>` : ''}
            ${rhStr ? `<div><strong>RH:</strong> <span style="color: #38bdf8;">${rhStr}</span></div>` : ''}
            ${dewStr ? `<div><strong>Dew:</strong> <span style="color: #67e8f9;">${dewStr}</span></div>` : ''}
            ${windStr ? `<div><strong>Wind:</strong> <span style="color: #4ade80;">${windStr}</span></div>` : ''}
            ${precipStr ? `<div><strong>Precip:</strong> <span style="color: #38bdf8;">${precipStr}</span></div>` : ''}
            ${pressStr ? `<div><strong>Pres:</strong> <span style="color: #c084fc;">${pressStr}</span></div>` : ''}
            ${props.flt_cat ? `<div><strong>Cat:</strong> <span style="font-weight: 800;">${props.flt_cat}</span></div>` : ''}
          </div>
          <div style="margin-top: 5px; padding-top: 4px; border-top: 1px solid rgba(148,163,184,0.16); font-size: 10.5px; color: #38bdf8; font-weight: 600;">
            Click for Today / 7D / 14D trend charts
          </div>
        </div>
      `;

      this.clearPopup();
      this.hoverPopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 10,
        className: 'wx-station-hover-popup'
      })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(this.map);
    };

    const onLeave = () => {
      this.map.getCanvas().style.cursor = '';
      this.clearPopup();
    };

    const onClick = (e: maplibregl.MapLayerMouseEvent) => {
      const feature = e.features && e.features[0];
      if (!feature) return;
      const coords = (feature.geometry as any).coordinates;
      const props = {
        ...(feature.properties as any),
        lon: coords[0],
        lat: coords[1]
      } as WxStationFeatureProps;

      let tab: 'temp' | 'precip' | 'wind' | 'pressure' = 'temp';
      if (this.activeMetric === 'wind') tab = 'wind';
      else if (this.activeMetric === 'precip') tab = 'precip';
      else if (this.activeMetric === 'pressure') tab = 'pressure';

      this.clearPopup();
      wxStationModal.open(props, tab);
    };

    for (const lid of targetLayers) {
      this.map.on('mouseenter', lid, onEnter);
      this.map.on('mouseleave', lid, onLeave);
      this.map.on('click', lid, onClick);
    }
  }
}

export default WxStationsLayer;
